import { SagaIterator } from "redux-saga";
import { all, call, fork, put, select, takeLatest } from "redux-saga/effects";

import {
  FETCH_ALL_BILLING_DATA,
  FETCH_BILLING_ACCOUNT_LIST,
  FETCH_BILLING_DATA,
  FETCH_CREDENTIALS,
  FETCH_CURRENT_PRINCIPAL,
  LINK_CREDENTIAL,
  REMOVE_BILLING_DATA,
  UNLINK_CREDENTIAL,
} from "./constants";
import userAPIService from "../../services/api/user";
import { isPhSubscriptionManaged } from "../../constants/spCode";
import Toaster from "../../services/toaster";
import {
  fetchAllBillingDataSuccess,
  fetchBillingAccountList,
  fetchBillingAccountListError,
  fetchBillingAccountListSuccess,
  fetchBillingDataError,
  fetchBillingDataSuccess,
  fetchCredentials,
  fetchCredentialsError,
  fetchCredentialsSuccess,
  fetchCurrentCustomerError,
  fetchCurrentCustomerSuccess,
  fetchCurrentPrincipalError,
  fetchCurrentPrincipalSuccess,
  fetchingCurrentCustomer,
  fetchingCurrentPrincipal,
  linkCredentialError,
  linkCredentialSuccess,
  removeBillingDataError,
  removeBillingDataSuccess,
  unlinkCredentialError,
  unlinkCredentialSuccess,
} from "./actions";
import { selectBillingAccountList, selectPrincipalData } from "./selectors";
import {
  IFetchAllBillingData,
  IFetchBillingAccountList,
  IFetchBillingData,
  IFetchCredentials,
  ILinkCredential,
  IPrincipalFetchAction,
  IRemoveBillingData,
  IUnlinkCredential,
} from "./types";
import { IBillingAccount, IBillingInfo } from "../../services/api/user/types";

export function* fetchPrincipal(principalId: string): SagaIterator {
  yield put(fetchingCurrentPrincipal());

  try {
    const principal = yield call(
      [userAPIService, userAPIService.get],
      principalId,
    );
    yield put(fetchCurrentPrincipalSuccess(principal));
  } catch (error) {
    yield put(fetchCurrentPrincipalError(error));
  }
}

export function* fetchCustomer(customerId: string): SagaIterator {
  yield put(fetchingCurrentCustomer());

  try {
    const principal = yield call(
      [userAPIService, userAPIService.getCustomer],
      customerId,
    );

    yield put(fetchCurrentCustomerSuccess(principal));
  } catch (error) {
    yield put(fetchCurrentCustomerError(error));
  }
}

function* fetchBillingAccounts({
  payload: customerId,
}: IFetchBillingAccountList): SagaIterator {
  try {
    const billingAccountList = yield call(
      [userAPIService, userAPIService.getBillingAccounts],
      customerId,
    );

    yield put(fetchBillingAccountListSuccess(billingAccountList));
  } catch (error) {
    yield put(fetchBillingAccountListError(error));
  }
}

export function* fetchPrincipalAndCustomerWithBillingAccount({
  payload,
}: IPrincipalFetchAction): SagaIterator {
  yield call(fetchPrincipal, payload);

  const userData = yield select(selectPrincipalData);

  yield put(fetchBillingAccountList(userData.customer_id));
  yield put(
    fetchCredentials({
      customerId: userData.customer_id,
      principalId: userData.id,
    }),
  );
  yield call(fetchCustomer, userData.customer_id);
}

function* fetchBillingData({ payload }: IFetchBillingData): SagaIterator {
  try {
    const billingData = yield call(
      [userAPIService, userAPIService.getBillingInfo],
      payload.spCode,
      payload.customerId,
    );

    yield put(fetchBillingDataSuccess(billingData));
  } catch (error) {
    yield put(fetchBillingDataError(error));
  }
}

function* fetchBillingInfoForAccount(
  spCode: string,
  customerId: string,
): SagaIterator {
  // A PH-subscription-service sub (crypto/BTCPay) has no card on file and its
  // platform billing-info endpoint returns 404, so skip the call entirely —
  // surface it as "managed by provider".
  if (isPhSubscriptionManaged(spCode)) {
    return { spCode, info: null };
  }
  try {
    const info = yield call(
      [userAPIService, userAPIService.getBillingInfo],
      spCode,
      customerId,
    );

    return { spCode, info };
  } catch (error) {
    // Apple/Google billing is store-managed and has no card on file, so a 404
    // here is expected — surface those accounts as "managed by provider" instead.
    return { spCode, info: null };
  }
}

function* fetchAllBillingData({ payload }: IFetchAllBillingData): SagaIterator {
  const accounts: IBillingAccount[] =
    (yield select(selectBillingAccountList)) ?? [];

  const results = yield all(
    accounts.map((account) => {
      return call(fetchBillingInfoForAccount, account.sp_code, payload);
    }),
  );

  const billingDataBySpCode: Record<string, IBillingInfo> = {};
  (results as Array<{ spCode: string; info: IBillingInfo | null }>).forEach(
    (result) => {
      if (result.info) {
        billingDataBySpCode[result.spCode] = result.info;
      }
    },
  );

  yield put(fetchAllBillingDataSuccess(billingDataBySpCode));
}

function* removeBillingData({
  payload,
  onSuccess,
  onError,
  onFinally,
}: IRemoveBillingData): SagaIterator {
  try {
    yield call(
      [userAPIService, userAPIService.removeBillingInfo],
      payload.spCode,
      payload.customerId,
    );
    onSuccess();
    yield put(removeBillingDataSuccess());
  } catch (error) {
    onError();
    yield put(removeBillingDataError(error));
  } finally {
    if (onFinally) {
      onFinally();
    }
  }
}

function* fetchCredentialsSaga({ payload }: IFetchCredentials): SagaIterator {
  try {
    const response = yield call(
      [userAPIService, userAPIService.getCredentials],
      payload.customerId,
      payload.principalId,
    );

    yield put(fetchCredentialsSuccess(response.items));
  } catch (error) {
    yield put(fetchCredentialsError(error));
  }
}

function* linkCredentialSaga({ payload }: ILinkCredential): SagaIterator {
  try {
    yield call(
      [userAPIService, userAPIService.linkCredential],
      payload.customerId,
      payload.principalId,
      payload.authorityCode,
      payload.jwt,
    );

    yield put(linkCredentialSuccess());
    yield put(
      fetchCredentials({
        customerId: payload.customerId,
        principalId: payload.principalId,
      }),
    );
  } catch (error) {
    yield put(linkCredentialError(error));
    const serverMessage = (error as any)?.response?.data?.error;
    if (serverMessage) {
      Toaster.error({ content: serverMessage });
    } else {
      Toaster.error({ translationKey: "linkError" });
    }
  }
}

function* unlinkCredentialSaga({ payload }: IUnlinkCredential): SagaIterator {
  try {
    yield call(
      [userAPIService, userAPIService.unlinkCredential],
      payload.customerId,
      payload.principalId,
      payload.credentialId,
    );

    yield put(unlinkCredentialSuccess());
    yield put(
      fetchCredentials({
        customerId: payload.customerId,
        principalId: payload.principalId,
      }),
    );
    Toaster.success({ translationKey: "unlinkSuccess" });
  } catch (error) {
    yield put(unlinkCredentialError(error));
    Toaster.error({ translationKey: "unlinkError" });
  }
}

export function* watchFetchPrincipal(): SagaIterator {
  yield takeLatest(
    FETCH_CURRENT_PRINCIPAL,
    fetchPrincipalAndCustomerWithBillingAccount,
  );
}

export function* watchFetchAccountList(): SagaIterator {
  yield takeLatest(FETCH_BILLING_ACCOUNT_LIST, fetchBillingAccounts);
}

export function* watchFetchBillingData(): SagaIterator {
  yield takeLatest(FETCH_BILLING_DATA, fetchBillingData);
}

export function* watchFetchAllBillingData(): SagaIterator {
  yield takeLatest(FETCH_ALL_BILLING_DATA, fetchAllBillingData);
}

export function* watchRemoveBillingData(): SagaIterator {
  yield takeLatest(REMOVE_BILLING_DATA, removeBillingData);
}

export function* watchFetchCredentials(): SagaIterator {
  yield takeLatest(FETCH_CREDENTIALS, fetchCredentialsSaga);
}

export function* watchLinkCredential(): SagaIterator {
  yield takeLatest(LINK_CREDENTIAL, linkCredentialSaga);
}

export function* watchUnlinkCredential(): SagaIterator {
  yield takeLatest(UNLINK_CREDENTIAL, unlinkCredentialSaga);
}

export function* currentPrincipalSaga(): SagaIterator {
  yield all([
    fork(watchFetchPrincipal),
    fork(watchFetchAccountList),
    fork(watchFetchBillingData),
    fork(watchFetchAllBillingData),
    fork(watchRemoveBillingData),
    fork(watchFetchCredentials),
    fork(watchLinkCredential),
    fork(watchUnlinkCredential),
  ]);
}
